import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { Starfield } from "@/components/mystic/Starfield";
import { GoldButton } from "@/components/mystic/GoldButton";

const TITLE_LINES = [
  { text: "ANTES DE DESISTIR", gold: false },
  { text: "DESSE AMOR...", gold: false },
  { text: "DESCUBRA OS SEGREDOS", gold: true },
  { text: "DAS 50 SIMPATIAS", gold: true },
];

const BOOK_BADGES = ["50 Rituais", "Orações Especiais", "Bônus Exclusivos"];

export function Hero() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const startVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
    setPlaying(true);
  };

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-obsidian"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 90% 60% at 50% 0%, rgba(98,27,56,0.45), transparent 65%), linear-gradient(180deg, #12050C 0%, #350D23 45%, #12050C 100%)",
        }}
      />
      <Starfield stars={80} dust={26} />
      <div className="pointer-events-none absolute -bottom-24 left-[8%] h-72 w-72 rounded-full bg-dourado/15 blur-3xl anim-candle" />
      <div className="pointer-events-none absolute -bottom-16 right-[10%] h-56 w-56 rounded-full bg-rosa/15 blur-3xl anim-candle" style={{ animationDelay: "1.2s" }} />

      <div className="relative z-10 mx-auto w-full max-w-[900px] px-4 pb-20 pt-14 text-center sm:px-6 sm:pt-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-7 flex items-center justify-center gap-3"
        >
          <svg viewBox="0 0 64 64" className="h-8 w-8" aria-hidden="true">
            <path d="M42 8a26 26 0 1 0 12 40A30 30 0 0 1 42 8z" fill="none" stroke="#D6AF62" strokeWidth="3" />
            <circle cx="46" cy="16" r="2" fill="#D6AF62" />
          </svg>
          <span className="font-cinzel text-xs tracking-[0.4em] text-dourado">
            MEU EX DE VOLTA
          </span>
        </motion.div>

        <h1 className="font-serif text-3xl font-semibold leading-[1.1] tracking-tight text-creme sm:text-5xl lg:text-[3.4rem]">
          {TITLE_LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.gold ? "gold-text italic" : ""}`}
                style={line.gold ? { filter: "drop-shadow(0 0 14px rgba(214,175,98,0.45))" } : undefined}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.3 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto mt-9 overflow-hidden rounded-2xl border border-dourado/25 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8),0_0_50px_-12px_rgba(214,175,98,0.35)]"
          data-testid="vsl-container"
        >
          <video
            ref={videoRef}
            data-testid="vsl-video"
            poster="/images/vsl-poster.jpg"
            preload="metadata"
            playsInline
            controls={playing}
            className="aspect-video w-full bg-black"
          >
            <source src="/videos/vsl.mp4" type="video/mp4" />
            <source src="/videos/vsl.webm" type="video/webm" />
          </video>
          {!playing && (
            <button
              type="button"
              data-testid="vsl-play-button"
              onClick={startVideo}
              aria-label="Assistir ao vídeo"
              className="absolute inset-0 flex items-center justify-center bg-obsidian/25 transition-colors hover:bg-obsidian/15"
            >
              <span className="anim-glow absolute h-28 w-28 rounded-full bg-dourado/25 blur-2xl" aria-hidden="true" />
              <span className="relative flex h-20 w-20 items-center justify-center rounded-full border border-dourado/60 bg-gradient-to-br from-[#A47B2C] via-dourado to-[#F3E5C8] shadow-[0_0_40px_rgba(214,175,98,0.5)] transition-transform duration-300 hover:scale-110 sm:h-24 sm:w-24">
                <Play className="ml-1 h-9 w-9 fill-vinho text-vinho sm:h-11 sm:w-11" />
              </span>
            </button>
          )}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mx-auto mt-8 max-w-xl font-serif text-lg italic leading-relaxed text-rosa sm:text-xl"
        >
          “Conheça antigas tradições, rituais e simpatias do amor que atravessaram gerações.”
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.6 }}
          className="mt-8"
        >
          <GoldButton testId="hero-cta-button" priceSize="md" priceCaption="Acesso ao e-book digital em PDF">
            QUERO DESCOBRIR AS 50 SIMPATIAS
          </GoldButton>
          <ul className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs uppercase tracking-[0.18em] text-creme/65">
            <li data-testid="hero-badge-pdf">E-book digital em PDF</li>
            <li data-testid="hero-badge-access">Acesso após a confirmação do pagamento</li>
            <li data-testid="hero-badge-pages">60 páginas de conteúdo</li>
          </ul>
          <div
            data-testid="hero-livro-badges"
            className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[0.6rem] font-semibold uppercase tracking-[0.3em] text-dourado/80"
          >
            {BOOK_BADGES.map((badge, i) => (
              <span key={badge} className="flex items-center gap-5">
                {i > 0 && <span className="h-1 w-1 rounded-full bg-rosa/70" aria-hidden="true" />}
                <span>{badge}</span>
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
